import { BooleanDataFlowType, IntegerDataFlowType, StringDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { isTruthy } from 'app/model/dataflow/dataflow-expression-utilities';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { RestSettings } from 'app/model/dataflow/dataset-settings/rest-settings';
import { Dataset, DataFlowDSLAdapterRegistry, DataFlowParameterConfig, LinkedService, Metamodel, Owned } from 'shell-models-lib';
import { DataFlowAdapterUtils } from '../adapter.util';
import { DataFlowExpressionUtils } from '../expression.util';
import { BaseFormatAdapter } from './base-format.adapter';
import { DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { getAttributeValueFromDataset } from 'app/model/dataset/dataset.utils';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(RestSettings)
})
export class RestFormatAdapter extends BaseFormatAdapter<RestSettings> {
    /**
     * Supplements missing DSL properties for Fabric Migration.
     */
    public supplementPropsForFabricMigration(dataset: Dataset | undefined, _linkedService: LinkedService | undefined, props: string[], _isSource: boolean, isInline: boolean, _datasetSettings: DataFlowDatasetSettings | undefined): void {
        if (isInline || !dataset) {
            return;
        }

        props.push(`format: 'rest'`);

        const relativeUrl = getAttributeValueFromDataset(dataset, 'relativeUrl');
        if (relativeUrl) {
            props.push(`entity: ${DataFlowAdapterUtils.formatExpressionValue(relativeUrl)}`);
        }
    }

    protected async toSourceDSL(restSettings: RestSettings): Promise<string[]> {
        const props = [];
        props.push(...this._commonPropertiesToDsl(restSettings));
        if (restSettings.requestMethod.expression) {
            props.push(`httpMethod: ${DataFlowExpressionUtils.wrapExpressionConfigValue(restSettings.requestMethod)}`);
        }
        if (restSettings.requestMethod.expression !== 'GET' && restSettings.requestBody.expression) {
            props.push(`body: ${DataFlowExpressionUtils.wrapExpressionConfigValue(restSettings.requestBody)}`);
        }

        if (restSettings.paginationRules && restSettings.paginationRules.length) {
            const paginationRules = restSettings.paginationRules
                .filter((rule) => rule.name && rule.value)
                .map(
                    (rule) =>
                        `${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            rule.paramName as DataFlowExpression
                        )} -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            rule.paramValue as DataFlowExpression
                        )}`
                )
                .join(', ');
            if (paginationRules) {
                props.push(`paginationRules: [${paginationRules}]`);
            }
        }

        if (restSettings.restDataFormat === 'json') {
            const formatSettings = [`'type' -> 'json'`];
            formatSettings.push(
                `'documentForm' -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(restSettings.documentForm)}`
            );
            const formatSettingsInDsl = formatSettings.join(', ');
            if (formatSettingsInDsl) {
                props.push(`responseFormat: [${formatSettingsInDsl}]`);
            }
        } else if (restSettings.restDataFormat === 'xml') {
            const formatSettings = [`'type' -> 'xml'`];
            if (restSettings.nullValue) {
                formatSettings.push(`'nullValue' -> '${restSettings.nullValue}'`);
            }
            formatSettings.push(`'namespaces' -> ${restSettings.namespaces}`);
            if (restSettings.namespaces) {
                const namespacePrefixes = restSettings.namespacePrefixes
                    .filter((namespacePrefix) => namespacePrefix.key && namespacePrefix.val)
                    .map(
                        (namespacePrefix) =>
                            // eslint-disable-next-line max-len
                            `${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                (restSettings.owner as DataFlowSource).createTemplatableExpression(
                                    StringDataFlowType.TYPE,
                                    namespacePrefix.key
                                )
                            )}->${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                (restSettings.owner as DataFlowSink).createTemplatableExpression(
                                    StringDataFlowType.TYPE,
                                    namespacePrefix.val
                                )
                            )}`
                    )
                    .join(',');
                if (namespacePrefixes.length > 0) {
                    formatSettings.push(`'namespacePrefixes' -> [${namespacePrefixes}]`);
                }
            }
            const formatSettingsInDsl = formatSettings.join(', ');
            if (formatSettingsInDsl) {
                props.push(`responseFormat: [${formatSettingsInDsl}]`);
            }
        }
        return props;
    }

    protected async toSinkDSL(restSettings: RestSettings): Promise<string[]> {
        const props = [];
        props.push(
            `deletable:${DataFlowExpressionUtils.wrapExpressionConfigValue(
                restSettings.allowDelete,
                BooleanDataFlowType.TYPE
            )}`
        );
        props.push(
            `insertable:${DataFlowExpressionUtils.wrapExpressionConfigValue(
                restSettings.allowInsert,
                BooleanDataFlowType.TYPE
            )}`
        );
        props.push(
            `updateable:${DataFlowExpressionUtils.wrapExpressionConfigValue(
                restSettings.allowUpdate,
                BooleanDataFlowType.TYPE
            )}`
        );
        props.push(
            `upsertable:${DataFlowExpressionUtils.wrapExpressionConfigValue(
                restSettings.allowUpsert,
                BooleanDataFlowType.TYPE
            )}`
        );
        if (restSettings.allowInsert.expression && restSettings.insertHttpMethod.expression) {
            props.push(
                `insertHttpMethod: ${DataFlowExpressionUtils.wrapExpressionConfigValue(restSettings.insertHttpMethod)}`
            );
        }
        if (restSettings.allowDelete.expression && restSettings.deleteHttpMethod.expression) {
            props.push(
                `deleteHttpMethod: ${DataFlowExpressionUtils.wrapExpressionConfigValue(restSettings.deleteHttpMethod)}`
            );
        }
        if (restSettings.allowUpsert.expression && restSettings.upsertHttpMethod.expression) {
            props.push(
                `upsertHttpMethod: ${DataFlowExpressionUtils.wrapExpressionConfigValue(restSettings.upsertHttpMethod)}`
            );
        }
        if (restSettings.allowUpdate.expression && restSettings.updateHttpMethod.expression) {
            props.push(
                `updateHttpMethod: ${DataFlowExpressionUtils.wrapExpressionConfigValue(restSettings.updateHttpMethod)}`
            );
        }
        if (restSettings.restDataFormat && restSettings.batchSize.expression) {
            props.push(
                `batchSize: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    restSettings.batchSize,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        if (restSettings.httpCompressionType.expression) {
            props.push(
                `httpCompressionType: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    restSettings.httpCompressionType
                )}`
            );
        }
        if (restSettings.restDataFormat === 'json') {
            const formatSettings = [`'type' -> 'json'`];
            if (restSettings.dateFormat) {
                formatSettings.push(`'outputDateFormat' -> '${restSettings.dateFormat}'`);
            }
            if (restSettings.timestampFormat) {
                formatSettings.push(`'outputTimestampFormat' -> '${restSettings.timestampFormat}'`);
            }
            const formatSettingsInDsl = formatSettings.join(', ');
            if (formatSettingsInDsl) {
                props.push(`requestFormat: [${formatSettingsInDsl}]`);
            }
        }
        if (
            isTruthy(restSettings.allowInsert) ||
            isTruthy(restSettings.allowUpsert) ||
            isTruthy(restSettings.allowUpdate) ||
            isTruthy(restSettings.allowDelete)
        ) {
            if (restSettings.rowRelativeUrl && restSettings.rowRelativeUrl.expression) {
                props.push(
                    `rowRelativeUrl: ${DataFlowExpressionUtils.wrapExpressionConfigValue(restSettings.rowRelativeUrl)}`
                );
            }
            if (isTruthy(restSettings.skipRowRelativeUrl)) {
                props.push(
                    `skipRowRelativeUrl: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        restSettings.skipRowRelativeUrl,
                        BooleanDataFlowType.TYPE
                    )}`
                );
            }
        }
        props.push(...this._commonPropertiesToDsl(restSettings));
        return props;
    }

    protected async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<RestSettings> {
        const restSettings = Owned.create(RestSettings.MODEL, owner) as RestSettings;
        restSettings.initialize(undefined);
        restSettings.paginationRules = [];
        if (configs.has('httpMethod')) {
            restSettings.requestMethod = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('httpMethod'),
                owner
            );
            handledConfigs.push('httpMethod');
        }
        if (configs.has('body')) {
            restSettings.requestBody = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('body'), owner);
            handledConfigs.push('body');
        }
        if (configs.has('paginationRules')) {
            restSettings.paginationRules = configs
                .get('paginationRules')
                .slice(1, configs.get('paginationRules').length - 1)
                .split(DataFlowAdapterUtils.MAP_ARRAY_SPLITTER)
                .map((rule) => {
                    const splits = rule.split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER);
                    const dataFlowParameterConfig = new DataFlowParameterConfig();
                    dataFlowParameterConfig.paramName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                        splits[0].trim(),
                        owner
                    );
                    dataFlowParameterConfig.paramValue = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                        splits[1].trim(),
                        owner
                    );
                    return dataFlowParameterConfig;
                });
            handledConfigs.push('paginationRules');
        }
        if (configs.has('responseFormat')) {
            const formatSettings = configs
                .get('responseFormat')
                .slice(1, configs.get('responseFormat').length - 1)
                .split(DataFlowAdapterUtils.MAP_ARRAY_SPLITTER);
            formatSettings.forEach((setting) => {
                const [settingName, settingValue] = setting
                    .split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER)
                    .map((v) => v.trim());
                switch (settingName) {
                    case `'type'`:
                        restSettings.restDataFormat = DataFlowExpressionUtils.extractStringConfigValue(settingValue) as
                            | 'xml'
                            | 'json';
                        break;
                    case `'documentForm'`:
                        restSettings.documentForm = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                            settingValue,
                            owner
                        );
                        break;
                    case `'nullValue'`:
                        restSettings.nullValue = DataFlowExpressionUtils.extractStringConfigValue(settingValue);
                        break;
                    case `'namespaces'`:
                        restSettings.namespaces = settingValue === 'true';
                        break;
                    case `'namespacePrefixes'`:
                        restSettings.namespacePrefixes = settingValue
                            .slice(1, settingValue.length - 1)
                            .split(DataFlowAdapterUtils.MAP_ARRAY_SPLITTER)
                            .map((element) => {
                                const splits = element.split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER);
                                return {
                                    key: DataFlowExpressionUtils.unwrapExpressionConfigValue(splits[0].trim(), owner)
                                        .expression,
                                    val: DataFlowExpressionUtils.unwrapExpressionConfigValue(splits[1].trim(), owner)
                                        .expression
                                };
                            });
                        break;
                    default:
                }
            });
            handledConfigs.push('responseFormat');
        } else {
            restSettings.restDataFormat = undefined;
        }
        this._commonPropertiesToModel(owner, configs, handledConfigs, restSettings);
        return restSettings;
    }

    protected async toSinkModel(
        owner: DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<RestSettings> {
        const restSettings = Owned.create(RestSettings.MODEL, owner) as RestSettings;
        restSettings.initialize(undefined);
        if (configs.has('insertHttpMethod')) {
            restSettings.insertHttpMethod = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('insertHttpMethod'),
                owner
            );
            handledConfigs.push('insertHttpMethod');
        } else {
            restSettings.insertHttpMethod.expression = '';
        }
        if (configs.has('deleteHttpMethod')) {
            restSettings.deleteHttpMethod = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('deleteHttpMethod'),
                owner
            );
            handledConfigs.push('deleteHttpMethod');
        } else {
            restSettings.deleteHttpMethod.expression = '';
        }
        if (configs.has('upsertHttpMethod')) {
            restSettings.upsertHttpMethod = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('upsertHttpMethod'),
                owner
            );
            handledConfigs.push('upsertHttpMethod');
        } else {
            restSettings.upsertHttpMethod.expression = '';
        }
        if (configs.has('updateHttpMethod')) {
            restSettings.updateHttpMethod = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('updateHttpMethod'),
                owner
            );
            handledConfigs.push('updateHttpMethod');
        } else {
            restSettings.updateHttpMethod.expression = '';
        }
        if (configs.has('insertable')) {
            restSettings.allowInsert = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('insertable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('insertable');
        }
        if (configs.has('deletable')) {
            restSettings.allowDelete = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('deletable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('deletable');
        }
        if (configs.has('upsertable')) {
            restSettings.allowUpsert = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('upsertable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('upsertable');
        }
        if (configs.has('updateable')) {
            restSettings.allowUpdate = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('updateable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('updateable');
        }
        if (configs.has('httpCompressionType')) {
            restSettings.httpCompressionType = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('httpCompressionType'),
                owner
            );
            handledConfigs.push('httpCompressionType');
        }
        if (configs.has('requestFormat')) {
            const formatSettings = configs
                .get('requestFormat')
                .slice(1, configs.get('requestFormat').length - 1)
                .split(DataFlowAdapterUtils.MAP_ARRAY_SPLITTER);
            formatSettings.forEach((setting) => {
                const [settingName, settingValue] = setting
                    .split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER)
                    .map((v) => v.trim());
                switch (settingName) {
                    case `'type'`:
                        restSettings.restDataFormat = DataFlowExpressionUtils.extractStringConfigValue(settingValue) as
                            | 'json'
                            | 'xml';
                        break;
                    case `'outputDateFormat'`:
                        restSettings.dateFormat = DataFlowExpressionUtils.extractStringConfigValue(settingValue);
                        break;
                    case `'outputTimestampFormat'`:
                        restSettings.timestampFormat = DataFlowExpressionUtils.extractStringConfigValue(settingValue);
                        break;
                    default:
                }
            });
            handledConfigs.push('requestFormat');
        } else {
            restSettings.restDataFormat = undefined;
        }
        if (configs.has('rowRelativeUrl')) {
            const expression = configs.get('rowRelativeUrl');
            restSettings.rowRelativeUrl = DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, owner);
            handledConfigs.push('rowRelativeUrl');
        }
        if (configs.has('skipRowRelativeUrl')) {
            restSettings.skipRowRelativeUrl = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('skipRowRelativeUrl'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('skipRowRelativeUrl');
        }
        this._commonPropertiesToModel(owner, configs, handledConfigs, restSettings);
        return restSettings;
    }

    private _commonPropertiesToDsl(restSettings: RestSettings): string[] {
        const props = [`format: 'rest'`];
        if (restSettings.requestTimeout.expression) {
            props.push(
                `timeout: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    restSettings.requestTimeout,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        if (restSettings.requestInterval.expression) {
            props.push(
                `requestInterval: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    restSettings.requestInterval,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        if (restSettings.requestEntity.expression) {
            props.push(`entity: ${DataFlowExpressionUtils.wrapExpressionConfigValue(restSettings.requestEntity)}`);
        }
        if (restSettings.additionalHeaders && restSettings.additionalHeaders.length) {
            const headers = restSettings.additionalHeaders
                .filter((header) => header.name && header.value)
                .map(
                    (header) =>
                        `${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            header.paramName as DataFlowExpression
                        )} -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            header.paramValue as DataFlowExpression
                        )}`
                )
                .join(', ');
            if (headers) {
                props.push(`headers: [${headers}]`);
            }
        }
        if (restSettings.queryParameters && restSettings.queryParameters.length) {
            const queryParameters = restSettings.queryParameters
                .filter((p) => p.name)
                .map(
                    (p) =>
                        `${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            p.paramName as DataFlowExpression
                        )} -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(p.paramValue as DataFlowExpression)}`
                )
                .join(', ');
            if (queryParameters) {
                props.push(`queryParameters: [${queryParameters}]`);
            }
        }
        return props;
    }

    private _commonPropertiesToModel(
        owner: DataFlowSource | DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[],
        restSettings: RestSettings
    ): void {
        if (configs.has('timeout')) {
            restSettings.requestTimeout = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('timeout'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('timeout');
        }
        if (configs.has('requestInterval')) {
            restSettings.requestInterval = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('requestInterval'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('requestInterval');
        }
        if (configs.has('entity')) {
            restSettings.requestEntity = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('entity'),
                owner
            );
            handledConfigs.push('entity');
        }
        if (configs.has('headers')) {
            restSettings.additionalHeaders = configs
                .get('headers')
                .slice(1, configs.get('headers').length - 1)
                .split(DataFlowAdapterUtils.MAP_ARRAY_SPLITTER)
                .map((header) => {
                    const splits = header.split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER);
                    const dataFlowParameterConfig = new DataFlowParameterConfig();
                    dataFlowParameterConfig.paramName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                        splits[0].trim(),
                        owner
                    );
                    dataFlowParameterConfig.paramValue = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                        splits[1].trim(),
                        owner
                    );
                    return dataFlowParameterConfig;
                });
            handledConfigs.push('headers');
        }
        if (configs.has('queryParameters')) {
            restSettings.queryParameters = configs
                .get('queryParameters')
                .slice(1, configs.get('queryParameters').length - 1)
                .split(DataFlowAdapterUtils.MAP_ARRAY_SPLITTER)
                .map((para) => {
                    const splits = para.split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER);
                    const dataFlowParameterConfig = new DataFlowParameterConfig();
                    dataFlowParameterConfig.paramName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                        splits[0].trim(),
                        owner
                    );
                    dataFlowParameterConfig.paramValue = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                        splits[1].trim(),
                        owner
                    );
                    return dataFlowParameterConfig;
                });
            handledConfigs.push('queryParameters');
        }
    }
}
